import React, { useEffect, useState } from "react";
import { Box, CssBaseline, Grid, Slider, Typography } from "@mui/material";
import { mainLayoutStyles, showNotification } from "../../../../../constants";

import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import { Toast } from "../../../../../common/alert";
import QTDetailsTextFieldWithLabelSection from "../../../../../common/QTDetailsTextFieldWithLabelSection";
import {
  DEFAULT_FEED_CONFIG,
  getFeedRankingConfig,
  updateFeedRankingConfig,
} from "../../hooks/ListenFeedSettingsHook";

// Parse a text input value into a number, keeping empty string as fallback.
const toNumber = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

const ListenFeedSettingsContainer = () => {
  const [editEnabled, setEditEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ ...DEFAULT_FEED_CONFIG });

  useEffect(() => {
    handleGetConfig();
  }, []);

  const handleGetConfig = async () => {
    try {
      const data = await getFeedRankingConfig();
      setConfig(data);
    } catch (error) {
      console.log("error getting feedRanking config:", error.message);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const payload = {
        useInternalFeed:
          config.useInternalFeed === true || config.useInternalFeed === "true",
        useInternalSearch:
          config.useInternalSearch === true ||
          config.useInternalSearch === "true",
        lambdaDecayPerDay: toNumber(
          config.lambdaDecayPerDay,
          DEFAULT_FEED_CONFIG.lambdaDecayPerDay
        ),
        candidatePoolCap: toNumber(
          config.candidatePoolCap,
          DEFAULT_FEED_CONFIG.candidatePoolCap
        ),
        weights: {
          recency: toNumber(config.weights?.recency),
          popularity: toNumber(config.weights?.popularity),
          engagement: toNumber(config.weights?.engagement),
          affinity: toNumber(config.weights?.affinity),
          orgPriority: toNumber(config.weights?.orgPriority),
          qtPriority: toNumber(config.weights?.qtPriority),
          nearby: toNumber(config.weights?.nearby),
        },
        popSubWeights: {
          like: toNumber(config.popSubWeights?.like),
          save: toNumber(config.popSubWeights?.save),
          reaction: toNumber(config.popSubWeights?.reaction),
          share: toNumber(config.popSubWeights?.share),
          listen: toNumber(config.popSubWeights?.listen),
        },
        search: {
          fieldWeights: {
            title: toNumber(config.search?.fieldWeights?.title),
            narrator: toNumber(config.search?.fieldWeights?.narrator),
            creator: toNumber(config.search?.fieldWeights?.creator),
            org: toNumber(config.search?.fieldWeights?.org),
            description: toNumber(config.search?.fieldWeights?.description),
          },
          typeaheadLimit: toNumber(config.search?.typeaheadLimit),
          fullLimit: toNumber(config.search?.fullLimit),
        },
        updatedAt: new Date(),
      };
      await updateFeedRankingConfig(payload);
      await handleGetConfig();
      showNotification("Listen Feed & Search settings updated successfully.");
      setEditEnabled(false);
    } catch (error) {
      console.log("error updating feedRanking config:", error.message);
      Toast("error", "error updating Listen Feed & Search settings");
    } finally {
      setLoading(false);
    }
  };

  // Immutable nested state setters.
  const setTop = (key, value) => setConfig({ ...config, [key]: value });
  const setWeight = (key, value) =>
    setConfig({ ...config, weights: { ...config.weights, [key]: value } });
  const setPopSub = (key, value) =>
    setConfig({
      ...config,
      popSubWeights: { ...config.popSubWeights, [key]: value },
    });
  const setFieldWeight = (key, value) =>
    setConfig({
      ...config,
      search: {
        ...config.search,
        fieldWeights: { ...config.search?.fieldWeights, [key]: value },
      },
    });
  const setSearch = (key, value) =>
    setConfig({ ...config, search: { ...config.search, [key]: value } });

  const numberField = (label, value, onChange) => (
    <QTDetailsTextFieldWithLabelSection
      label={label}
      value={value}
      disabled={true}
      editEnabled={editEnabled}
      type={"number"}
      onChange={onChange}
    />
  );

  // Ranking-weight slider: 0..10 range, 0.1 increments, no visible tick marks.
  // `onChangeValue` receives the new numeric value directly.
  const weightSlider = (label, value, onChangeValue) => {
    const numericValue = toNumber(value);
    return (
      <Grid item style={weightSliderContainerStyles}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography style={weightSliderLabelStyles}>{label}</Typography>
          <Typography style={weightSliderValueStyles}>
            {numericValue.toFixed(1)}
          </Typography>
        </Grid>
        <Slider
          value={numericValue}
          min={0}
          max={10}
          step={0.1}
          marks={false}
          disabled={!editEnabled}
          valueLabelDisplay={"auto"}
          onChange={(_e, newValue) => onChangeValue(newValue)}
        />
      </Grid>
    );
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={"Listen Feed & Search Settings"}
        bgColor="#fff"
        buttonText={"Edit"}
        editEnabled={editEnabled}
        setEditEnabled={setEditEnabled}
        cancelButton={editEnabled ? true : false}
        handleSave={() => handleSave()}
        loading={loading}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"}>
          <Grid container style={dataContainerStyles} direction={"column"}>
            {/* Feature flags */}
            <Typography style={sectionTitleStyles}>Feature Flags</Typography>
            <Grid container>
              <Grid item container direction={"column"} xs>
                <QTDetailsTextFieldWithLabelSection
                  label={"Use Internal Feed"}
                  value={String(config.useInternalFeed)}
                  disabled={true}
                  editEnabled={editEnabled}
                  select={true}
                  noTextTransform={true}
                  dropdownValues={["true", "false"]}
                  onChange={(e) => setTop("useInternalFeed", e.target.value)}
                />
              </Grid>
              <Grid item container direction={"column"} xs>
                <QTDetailsTextFieldWithLabelSection
                  label={"Use Internal Search"}
                  value={String(config.useInternalSearch)}
                  disabled={true}
                  editEnabled={editEnabled}
                  select={true}
                  noTextTransform={true}
                  dropdownValues={["true", "false"]}
                  onChange={(e) => setTop("useInternalSearch", e.target.value)}
                />
              </Grid>
            </Grid>

            {/* General */}
            <Typography style={sectionTitleStyles}>General</Typography>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {numberField(
                  "Lambda Decay / Day",
                  config.lambdaDecayPerDay,
                  (e) => setTop("lambdaDecayPerDay", e.target.value)
                )}
              </Grid>
              <Grid item container direction={"column"} xs>
                {numberField(
                  "Candidate Pool Cap",
                  config.candidatePoolCap,
                  (e) => setTop("candidatePoolCap", e.target.value)
                )}
              </Grid>
            </Grid>

            {/* Ranking weights */}
            <Typography style={sectionTitleStyles}>Ranking Weights</Typography>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {weightSlider("Recency", config.weights?.recency, (v) =>
                  setWeight("recency", v)
                )}
                {weightSlider("Popularity", config.weights?.popularity, (v) =>
                  setWeight("popularity", v)
                )}
                {weightSlider("Engagement", config.weights?.engagement, (v) =>
                  setWeight("engagement", v)
                )}
              </Grid>
              <Grid item container direction={"column"} xs>
                {weightSlider("Affinity", config.weights?.affinity, (v) =>
                  setWeight("affinity", v)
                )}
                {weightSlider("Org Priority", config.weights?.orgPriority, (v) =>
                  setWeight("orgPriority", v)
                )}
                {weightSlider("QT Priority", config.weights?.qtPriority, (v) =>
                  setWeight("qtPriority", v)
                )}
                {weightSlider("Nearby", config.weights?.nearby, (v) =>
                  setWeight("nearby", v)
                )}
              </Grid>
            </Grid>


            {/* Popularity sub-weights */}
            <Typography style={sectionTitleStyles}>
              Popularity Sub-Weights
            </Typography>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {weightSlider("Like", config.popSubWeights?.like, (v) =>
                  setPopSub("like", v)
                )}
                {weightSlider("Save", config.popSubWeights?.save, (v) =>
                  setPopSub("save", v)
                )}
                {weightSlider("Reaction", config.popSubWeights?.reaction, (v) =>
                  setPopSub("reaction", v)
                )}
              </Grid>
              <Grid item container direction={"column"} xs>
                {weightSlider("Share", config.popSubWeights?.share, (v) =>
                  setPopSub("share", v)
                )}
                {weightSlider("Listen", config.popSubWeights?.listen, (v) =>
                  setPopSub("listen", v)
                )}
              </Grid>
            </Grid>

            {/* Search field weights */}
            <Typography style={sectionTitleStyles}>
              Search Field Weights
            </Typography>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {weightSlider("Title", config.search?.fieldWeights?.title, (v) =>
                  setFieldWeight("title", v)
                )}
                {weightSlider(
                  "Narrator",
                  config.search?.fieldWeights?.narrator,
                  (v) => setFieldWeight("narrator", v)
                )}
                {weightSlider(
                  "Creator",
                  config.search?.fieldWeights?.creator,
                  (v) => setFieldWeight("creator", v)
                )}
              </Grid>
              <Grid item container direction={"column"} xs>
                {weightSlider("Org", config.search?.fieldWeights?.org, (v) =>
                  setFieldWeight("org", v)
                )}
                {weightSlider(
                  "Description",
                  config.search?.fieldWeights?.description,
                  (v) => setFieldWeight("description", v)
                )}
              </Grid>
            </Grid>

            {/* Search limits */}
            <Typography style={sectionTitleStyles}>Search Limits</Typography>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {numberField(
                  "Typeahead Limit",
                  config.search?.typeaheadLimit,
                  (e) => setSearch("typeaheadLimit", e.target.value)
                )}
              </Grid>
              <Grid item container direction={"column"} xs>
                {numberField("Full Limit", config.search?.fullLimit, (e) =>
                  setSearch("fullLimit", e.target.value)
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ListenFeedSettingsContainer;

const dataContainerStyles = {
  borderRadius: 12,
  boxShadow: " 0px 0px 30px 0px rgba(80, 85, 136, 0.15)",
  background: "var(--gray-99, #FCFCFC)",
  padding: "40px 30px",
};

const weightSliderContainerStyles = {
  paddingRight: 24,
  marginBottom: 12,
};

const weightSliderLabelStyles = {
  color: "#020303",
  fontSize: 15,
  fontFamily: "Avenir Book, sans-serif",
};

const weightSliderValueStyles = {
  color: "#020303",
  fontSize: 15,
  fontWeight: 700,
  fontFamily: "Avenir Book, sans-serif",
};

const sectionTitleStyles = {
  color: "#020303",
  fontSize: 18,
  fontWeight: 700,
  fontFamily: "Avenir Book, sans-serif",
  marginTop: 8,
  marginBottom: 20,
};

